// ManagementHoursPage.jsx

// Dependencies
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import FutureHoursReport from '@/components/data/hours/FutureHoursReport';

// Services
import { getAllocationsFuturesHoursReport, getTotalWeeksAvailable } from '@/services/schedulingService';

// Styling
import { homeCss, managementCss, buttonCss } from '@digital_monorepo/styling';

const ManagementHoursPage = () => {
  const [data, setData] = useState([]);
  const [weeksToDisplay, setWeeksToDisplay] = useState(4); // Default is 4 weeks
  const [totalWeeks, setTotalWeeks] = useState(4);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchTotalWeeks = async () => {
      try {
        const weeks = await getTotalWeeksAvailable();
        setTotalWeeks(weeks);
      } catch (error) {
        console.error('Error fetching total weeks:', error);
      }
    };

    fetchTotalWeeks();
  }, []);

  const fetchReportData = useCallback(async (weeks) => {
    try {
      setIsLoading(true);
      console.log(`Fetching management report data with ${weeks} weeks`);
      // Set management=true to fetch only management level professionals (function_level >= 600000)
      const reportData = await getAllocationsFuturesHoursReport(true, false, weeks);
      setData(reportData);
    } catch (error) {
      console.error('Error fetching management hours report data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReportData(weeksToDisplay);
  }, [fetchReportData, weeksToDisplay]);

  const handleWeeksChange = (newWeeks) => {
    setWeeksToDisplay(newWeeks);
  };

  const generateWeeksOptions = () => {
    const options = [];
    for (let i = 2; i <= totalWeeks; i += 2) {
      options.push(
        <option key={i} value={i}>{i}</option>
      );
    }
    return options;
  };

  if (isLoading) {
    return (
      <div className={managementCss.managementContainer}>
        <div className={managementCss.loadingContainer}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('COMMON.LOADING')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={managementCss.pageContainer}>
    <div className={managementCss.managementContainer}>
      <div className={managementCss.managementHeader}>
        <h2>Management Hours</h2>
        <div className={managementCss.managementActions}>
          <div className={managementCss.weekSelector}>
            <label htmlFor="weeks-select" className={managementCss.formLabel}>{t('COMMON.WEEKS_TO_DISPLAY')}: </label>
            <select 
              id="weeks-select" 
              value={weeksToDisplay} 
              onChange={(e) => handleWeeksChange(Number(e.target.value))}
              className={`form-select form-select-sm ${managementCss.formSelect}`}
            >
              {generateWeeksOptions()}
            </select>
          </div>
        </div>
      </div>
      
      <div className={managementCss.tableResponsive}>
        {data && <FutureHoursReport data={data}/>}
        
        {!data || data.length === 0 ? (
          <div className={managementCss.noData}>No data available</div>
        ) : null}
      </div>
    </div>
    </div>
  );
};

export default ManagementHoursPage;